/*
 * window.jaioraBugs — bug-reporter widget. Loaded as `<script defer src="<base>/bugs/widget.js">` before the app's
 * module script: the recorder (fetch, console, clicks) is installed right here, on execution.
 *
 * Defaults are neutral. To get exactly the behavior facts had before the extraction, facts calls (verbatim values and a
 * ready-to-paste snippet — widget/FACTS_CONFIG.md):
 *   personalUrl    — PERSONAL_URL of facts' redact.ts (candidates, recruiter-chat, telegram, … vacancies/:id/side…)
 *   notPeopleUrl   — NOT_PEOPLE_URL of facts' known.ts (tasks, telegram/accounts, vacancies/:id/scan|criteria|terms|updates)
 *   piiSelectors   — facts' PII_SELECTOR classes ('.chat-text', '.qna-text', … '.similar-snippet'; `[data-pii]` is built in)
 *   skipSelectors  — ['.tab-hidden']
 *   ownSelectors   — ['.nav-burger', '.side-nav']
 *   secretPatterns — [/\bcfacts_[A-Za-z0-9_-]{16,}/g]
 *   srcPrefix      — 'web/src/'
 *   makeRoom       — { marginRight: ['.layout'], right: ['.page-full', '.modal-overlay'] }
 *   appState       — () => ({ ui: <'new' | 'old'> })
 *   onOpenUxSessions — () => navigate('/settings#ux-sessions')
 *   dialog         — { shotButton: false, imageButton: false, maskSwitch: false, dropImagesAsShots: true,
 *                      requireExpected: true, inlineShotMarks: true, agentChat: false, voice: false, files: false,
 *                      recordSwitch: false, scenarioButton: false, htmlSwitch: false }
 *                    (reports 69 / 72 / 76; absent — the panel as before)
 *   launcher       — signed in: { bugs: <proxy configured>, screenMask: 'default-on', ask: { path: '/api/code-questions', sourceAttr: 'data-src',
 *                      depth: true, history: 4, hints: true } };
 *                    signed out: null (the help button in the bottom right corner is the widget's own);
 *                    `openTracker` — the "Репорты и вопросы" item (community: the host's /bug-reports page)
 *   whatsNew       — { service: true, open: () => navigate('/bug-reports/whats-new'), navigate } — the "Что нового"
 *                    menu item and screen (`mountWhatsNew(el)` on the host page); notes and "Где это?" — what the host's
 *                    deploy published to the service (community since 28.10); `path` — the host serves them itself
 *   settings       — { prompts: true } — prompt editors on the tracker screen (absent — no LLM instructions anywhere)
 */
import { configureRedact } from '../../shared/redact';
import { setConfig, type WidgetConfig } from './config';
import { installBugRecorder, noteSelection } from './recorder';
import { resumeGuide } from './whatsNew/placeGuide';
import { getState, mountSettings, mountWhatsNew, open, ready, startUxWatch, subscribe, watchConfig } from './runtime';
import { mountTracker } from './tracker/mount';

const VERSION = typeof __WIDGET_VERSION__ === 'string' ? __WIDGET_VERSION__ : 'dev';

export interface JaioraBugs {
  configure(config: WidgetConfig): void;
  ready: typeof ready;
  open: typeof open;
  startUxWatch: typeof startUxWatch;
  mountSettings: typeof mountSettings;
  /** "Что нового" screen on a host page (`whatsNew` of `configure()`). */
  mountWhatsNew: typeof mountWhatsNew;
  /** The tracker site (tracker.jaiora.me): login and the tracker screen in `el`. */
  mountTracker: typeof mountTracker;
  noteSelection: typeof noteSelection;
  /** Extra to the plan: what the host menu shows (draft open, watch on) and a change listener. */
  state: typeof getState;
  subscribe: typeof subscribe;
  version: string;
}

declare global {
  interface Window {
    jaioraBugs?: JaioraBugs;
  }
}

function install(): void {
  if (typeof window === 'undefined') return;
  // Loaded twice (two tags, a dev reload): the first copy keeps the page; a second recorder would record everything twice.
  if (window.jaioraBugs?.version) return;
  configureRedact({ personalUrl: null, secretPatterns: [] });
  installBugRecorder();
  watchConfig();
  window.jaioraBugs = {
    configure: (config) => setConfig(config ?? {}),
    ready,
    open,
    startUxWatch,
    mountSettings,
    mountWhatsNew,
    mountTracker,
    noteSelection,
    state: getState,
    subscribe,
    version: VERSION,
  };
  // Report 35: a note's place clicked before a full page load is shown once the page has drawn it.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', resumeGuide, { once: true });
  else resumeGuide();
}

install();
